/**
 * Device-local open-response writing draft — not Training Attempt evidence.
 * localStorage (same class as questionNotes): text drafts + in-progress AI grade session.
 */

export type WritingDraftScope = {
  languageId: string;
  examType: string;
  skillId: string;
};

/** Serializable AI grade snapshot (mirrors WritingGradeView; kept local to avoid feature↔data cycles). */
export type WritingDraftGradeSnapshot = {
  score: number;
  scoreMax: number;
  dimensionMax: number;
  scaleLabel: string;
  levelHint: string;
  feedback: string;
  strengths: string[];
  improvements: string[];
  dimensions: Array<{
    id: string;
    label: string;
    score: number;
    action: string;
  }>;
  isHeuristic: boolean;
};

export type WritingDraftGradeSession = {
  gradeResult: WritingDraftGradeSnapshot;
  firstDraft: string | null;
  firstGrade: WritingDraftGradeSnapshot | null;
  isRevising: boolean;
  hasCompletedRevision: boolean;
  revisionCredit: string | null;
};

export type WritingDraftRecord = WritingDraftScope & {
  id: string;
  questionId: string;
  essay: string;
  updatedAt: number;
  /** In-progress AI grade UI state — survives refresh; cleared on Attempt complete. */
  gradeSession?: WritingDraftGradeSession | null;
};

const STORAGE_KEY = 'examflow_writing_drafts';

export function buildWritingDraftId(scope: WritingDraftScope, questionId: string): string {
  return `${scope.languageId}::${scope.examType}::${scope.skillId}::${questionId}`;
}

function normalizeScope(scope: WritingDraftScope): WritingDraftScope | null {
  const languageId = scope.languageId.trim();
  const examType = scope.examType.trim().toLowerCase();
  const skillId = scope.skillId.trim().toLowerCase();
  if (!languageId || !examType || !skillId) return null;
  return { languageId, examType, skillId };
}

function isGradeSnapshot(value: unknown): value is WritingDraftGradeSnapshot {
  if (typeof value !== 'object' || value == null) return false;
  const row = value as Record<string, unknown>;
  return (
    typeof row.score === 'number' &&
    typeof row.scoreMax === 'number' &&
    typeof row.dimensionMax === 'number' &&
    typeof row.scaleLabel === 'string' &&
    typeof row.levelHint === 'string' &&
    typeof row.feedback === 'string' &&
    Array.isArray(row.strengths) &&
    Array.isArray(row.improvements) &&
    Array.isArray(row.dimensions) &&
    typeof row.isHeuristic === 'boolean'
  );
}

function parseGradeSession(value: unknown): WritingDraftGradeSession | null {
  if (typeof value !== 'object' || value == null) return null;
  const row = value as Record<string, unknown>;
  if (!isGradeSnapshot(row.gradeResult)) return null;
  const firstGrade =
    row.firstGrade == null ? null : isGradeSnapshot(row.firstGrade) ? row.firstGrade : null;
  return {
    gradeResult: row.gradeResult,
    firstDraft: typeof row.firstDraft === 'string' ? row.firstDraft : null,
    firstGrade,
    isRevising: row.isRevising === true,
    hasCompletedRevision: row.hasCompletedRevision === true,
    revisionCredit: typeof row.revisionCredit === 'string' ? row.revisionCredit : null,
  };
}

function isDraftRecord(item: unknown): item is WritingDraftRecord {
  if (typeof item !== 'object' || item == null) return false;
  const row = item as Record<string, unknown>;
  return (
    typeof row.id === 'string' &&
    typeof row.languageId === 'string' &&
    typeof row.examType === 'string' &&
    typeof row.skillId === 'string' &&
    typeof row.questionId === 'string' &&
    typeof row.essay === 'string' &&
    typeof row.updatedAt === 'number'
  );
}

function readAll(): WritingDraftRecord[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isDraftRecord).map((row) => ({
      ...row,
      gradeSession: row.gradeSession ? parseGradeSession(row.gradeSession) : null,
    }));
  } catch {
    return [];
  }
}

function writeAll(records: WritingDraftRecord[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(records));
}

function matchesScope(record: WritingDraftRecord, scope: WritingDraftScope): boolean {
  return (
    record.languageId === scope.languageId &&
    record.examType === scope.examType &&
    record.skillId === scope.skillId
  );
}

export type WritingDraftUpsertOptions = {
  /**
   * `undefined` — keep existing gradeSession (essay typing must not wipe AI result).
   * `null` — clear gradeSession.
   * object — replace gradeSession.
   */
  gradeSession?: WritingDraftGradeSession | null;
};

export const writingDraftStore = {
  async get(
    scope: WritingDraftScope,
    questionId: string
  ): Promise<WritingDraftRecord | null> {
    const normalized = normalizeScope(scope);
    const qid = questionId.trim();
    if (!normalized || !qid) return null;
    const id = buildWritingDraftId(normalized, qid);
    return readAll().find((row) => row.id === id) ?? null;
  },

  async getLatest(scope: WritingDraftScope): Promise<WritingDraftRecord | null> {
    const normalized = normalizeScope(scope);
    if (!normalized) return null;
    const rows = readAll()
      .filter((row) => matchesScope(row, normalized))
      .sort((a, b) => b.updatedAt - a.updatedAt);
    return rows[0] ?? null;
  },

  /**
   * Persist non-empty essay; empty/whitespace clears the draft slot.
   * Returns updatedAt on upsert, null on delete / no-op.
   */
  async upsert(
    scope: WritingDraftScope,
    questionId: string,
    essay: string,
    options?: WritingDraftUpsertOptions
  ): Promise<number | null> {
    const normalized = normalizeScope(scope);
    const qid = questionId.trim();
    if (!normalized || !qid) return null;

    const trimmed = essay.trim();
    if (!trimmed) {
      await this.delete(normalized, qid);
      return null;
    }

    const updatedAt = Date.now();
    const id = buildWritingDraftId(normalized, qid);
    const existing = readAll().find((row) => row.id === id);
    const gradeSession =
      options && 'gradeSession' in options
        ? options.gradeSession ?? null
        : (existing?.gradeSession ?? null);

    const next: WritingDraftRecord = {
      id,
      ...normalized,
      questionId: qid,
      essay,
      updatedAt,
      gradeSession,
    };
    const rows = readAll().filter((row) => row.id !== id);
    rows.push(next);
    writeAll(rows);
    return updatedAt;
  },

  async delete(scope: WritingDraftScope, questionId: string): Promise<void> {
    const normalized = normalizeScope(scope);
    const qid = questionId.trim();
    if (!normalized || !qid) return;
    const id = buildWritingDraftId(normalized, qid);
    writeAll(readAll().filter((row) => row.id !== id));
  },
};
